import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { color, space } from '@op/opds.tokens';
import { Message } from '@messageformat/react';
import { List, ListItem, Tile } from '@op/opds.opux';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import { ReactComponent as Circle } from '../../images/circle.svg';
import { ReactComponent as Rectangle } from '../../images/rectangle.svg';
import { ReactComponent as Triangle } from '../../images/triangle-gray.svg';
import { ReactComponent as RectangleTilted } from '../../images/rectangle-tilted.svg';
import { stockData, forestData, realEstateData, interestData } from './assetData';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import { formatNumberWithoutDecimals } from '../../utils';
import {
  getUrlEncodedCircle,
  getUrlEncodedDiamond,
  getUrlEncodedSquare,
  getUrlEncodedTriangle,
} from '../../util/svgIcons';

const AssetClassReturnsGraph = (): JSX.Element => {
  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: undefined,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
      min: 0,
      labels: {
        enabled: true,
        allowOverlap: false,
        formatter: (label: Highcharts.AxisLabelsFormatterContextObject) => {
          return `${formatNumberWithoutDecimals(Number(label.value) ? Number(label.value) : 0)} €`;
        },
      },
    },
    xAxis: {
      opposite: false,
      type: 'datetime',
      lineWidth: 0,
      minorGridLineWidth: 0,
      tickInterval: 365 * 24 * 3600 * 1000 * 12,
      units: [['year', [1]]],
      minorTickLength: 0,
      tickLength: 0,
    },
    series: [
      {
        lineColor: color.indicator.default,
        data: stockData,
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.default),
        },
      },
      {
        lineColor: color.indicator.positive,
        data: interestData,
        marker: {
          symbol: getUrlEncodedSquare(color.indicator.positive),
        },
      },
      {
        lineColor: color.icon.default,
        data: realEstateData,
        marker: {
          symbol: getUrlEncodedTriangle(color.icon.default),
        },
      },
      {
        lineColor: color.indicator.notification,
        data: forestData,
        marker: {
          symbol: getUrlEncodedDiamond(color.indicator.notification, '.7rem'),
        },
      },
    ] as Highcharts.SeriesOptionsType[],
  });

  return (
    <div className="investoracademy-graph-wrapper">
      <Tile style={{ marginTop: space.default }}>
        <h4 className="investoracademy-general-graph-title">
          <Message id="course2.asset_class_returns_graph.title" />
        </h4>
        <p className="investoracademy-general-graph-description">
          <Message id="course2.asset_class_returns_graph.label" />
        </p>
        <div style={{ display: 'none' }} id="asset_class_returns_graph">
          <Message id="course2.asset_class_returns_graph.asset_acccessibility_text" />
        </div>
        <div aria-labelledby="asset_class_returns_graph" role="img">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <List
            border="none"
            size="tiny"
            indented
            style={{ display: 'inline-block', textAlign: 'left', marginTop: 0 }}
            aria-hidden="true"
          >
            <ListItem style={{ display: 'inline' }}>
              <div className="investoracademy-listitem-container">
                <Circle fill={color.indicator.default} aria-hidden="true" />
                <Message id="course2.asset_class_returns_graph.stocks" />
              </div>
            </ListItem>
            <ListItem style={{ display: 'inline' }}>
              <div className="investoracademy-listitem-container">
                <Rectangle aria-hidden="true" fill={color.indicator.positive} />
                <Message id="course2.asset_class_returns_graph.interest" />
              </div>
            </ListItem>
            <ListItem style={{ display: 'inline' }}>
              <div className="investoracademy-listitem-container">
                <Triangle aria-hidden="true" />
                <Message id="course2.asset_class_returns_graph.real_estate" />
              </div>
            </ListItem>
            <ListItem style={{ display: 'inline' }}>
              <div className="investoracademy-listitem-container">
                <RectangleTilted fill={color.indicator.notification} aria-hidden="true" />
                <Message id="course2.asset_class_returns_graph.forest" />
              </div>
            </ListItem>
          </List>
        </div>
      </Tile>
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default }}
      >
        <IconInformation />
        <Message id="course2.asset_class_returns_graph.sources" />
      </p>
    </div>
  );
};

export default AssetClassReturnsGraph;
